import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../public/TGT_Logo.png";
import { useAuth } from "../context/AuthContext";
import "../styles/Home.css";
import useApiStable from "../utils/useApiStable";
import BockModal from "./bock/BockModal";
import { isUserAdmin } from "../utils/userUtils";

const Home = () => {
  const { user } = useAuth();
  const api = useApiStable();
  const navigate = useNavigate();

  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [matchesError, setMatchesError] = useState(null);
  const [dayTick, setDayTick] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const [myTeams, setMyTeams] = useState([]);
  const [myTeamMatches, setMyTeamMatches] = useState({});
  const [teamsLoading, setTeamsLoading] = useState(false);

  const [myWorkDuties, setMyWorkDuties] = useState([]);
  const [workDutiesLoading, setWorkDutiesLoading] = useState(false);

  const [myClubEvents, setMyClubEvents] = useState([]);
  const [clubEventsLoading, setClubEventsLoading] = useState(false);

  const [myBock, setMyBock] = useState(null);
  const [othersWithBockCount, setOthersWithBockCount] = useState(0);
  const [showBockModal, setShowBockModal] = useState(false);
  const [bockError, setBockError] = useState(null);

  const [latestNews, setLatestNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [readNewsIds, setReadNewsIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("readNewsIds") || "[]")); }
    catch { return new Set(); }
  });

  useEffect(() => {
    const now = new Date();
    const nextMidnight = new Date(now);
    nextMidnight.setHours(24, 0, 1, 0);
    const timer = setTimeout(() => setDayTick((t) => t + 1), nextMidnight - now);
    return () => clearTimeout(timer);
  }, [dayTick]);

  // PWA-Refresh: Wird die App nach laengerer Hintergrundzeit wieder sichtbar,
  // alle Startseiten-Daten neu laden. Timer (setTimeout/setInterval) werden im
  // Hintergrund gedrosselt und sind daher nicht verlaesslich; visibilitychange
  // feuert dagegen zuverlaessig beim Zurueckkehren in die App. Die 60s-Schwelle
  // verhindert, dass kurzes Wegtippen unnoetige Requests ausloest.
  useEffect(() => {
    let hiddenSince = null;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenSince = Date.now();
      } else if (document.visibilityState === "visible") {
        if (hiddenSince === null || Date.now() - hiddenSince > 60000) {
          setReloadKey((k) => k + 1);
        }
        hiddenSince = null;
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoadingMatches(true);
    api
      .get("/public/upcoming-matches")
      .then((data) => {
        if (!isMounted) return;
        setUpcomingMatches(Array.isArray(data) ? data : []);
        setMatchesError(null);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error("Fehler beim Laden der anstehenden Matches:", err);
        setMatchesError("Anstehende Matches konnten nicht geladen werden.");
      })
      .finally(() => {
        if (!isMounted) return;
        setLoadingMatches(false);
      });

    return () => {
      isMounted = false;
    };
  }, [api, dayTick, reloadKey]);

  useEffect(() => {
    let isMounted = true;

    if (!user) {
      setDashboard(null);
      setDashboardLoading(false);
      setDashboardError(null);
      return () => {
        isMounted = false;
      };
    }

    setDashboardLoading(true);
    api
      .get("/public/home-dashboard")
      .then((data) => {
        if (!isMounted) return;
        setDashboard(data ?? { registeredTournaments: [] });
        setDashboardError(null);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error("Fehler beim Laden der persönlichen Startseiteninfos:", err);
        setDashboard(null);
        setDashboardError("Persönliche Turnierinformationen konnten nicht geladen werden.");
      })
      .finally(() => {
        if (!isMounted) return;
        setDashboardLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [api, user, dayTick, reloadKey]);

  useEffect(() => {
    if (!user) {
      setMyTeams([]);
      setMyTeamMatches({});
      return;
    }

    setTeamsLoading(true);
    api.get("/league/teams/my")
      .then(async (teams) => {
        const matchesByTeam = {};
        await Promise.all(teams.map(async (team) => {
          try {
            const matches = await api.get(`/league/teams/${team.id}/matches`);
            const future = matches.filter(m => m.future);
            matchesByTeam[team.id] = future.slice(0, 3);
          } catch {
            matchesByTeam[team.id] = [];
          }
        }));
        // Mannschaften ohne anstehende Spiele werden auf der Startseite ausgeblendet
        setMyTeams(teams.filter(team => matchesByTeam[team.id].length > 0));
        setMyTeamMatches(matchesByTeam);
      })
      .catch(() => {
        setMyTeams([]);
      })
      .finally(() => setTeamsLoading(false));
  }, [api, user, dayTick, reloadKey]);

  useEffect(() => {
    if (!user) {
      setMyWorkDuties([]);
      return;
    }
    setWorkDutiesLoading(true);
    api.get('/workduty/appointments')
      .then(data => setMyWorkDuties(Array.isArray(data) ? data.slice(0, 5) : []))
      .catch(() => setMyWorkDuties([]))
      .finally(() => setWorkDutiesLoading(false));
  }, [api, user, dayTick, reloadKey]);

  useEffect(() => {
    if (!user) {
      setMyClubEvents([]);
      return;
    }
    setClubEventsLoading(true);
    api.get('/appointments/club-events/dashboard')
      .then(data => setMyClubEvents(Array.isArray(data) ? data : []))
      .catch(() => setMyClubEvents([]))
      .finally(() => setClubEventsLoading(false));
  }, [api, user, dayTick, reloadKey]);

  useEffect(() => {
    let isMounted = true;
    api.get("/news").then(data => {
      if (!isMounted) return;
      setLatestNews(Array.isArray(data) ? data.slice(0, 3) : []);
    }).catch(() => {}).finally(() => {
      if (!isMounted) return;
      setNewsLoading(false);
    });
    return () => { isMounted = false; };
  }, [api, reloadKey]);

  const markNewsRead = (id) => {
    setReadNewsIds(prev => {
      const next = new Set(prev);
      next.add(String(id));
      localStorage.setItem("readNewsIds", JSON.stringify([...next]));
      return next;
    });
  };

  const loadBock = useCallback(async () => {
    if (!user) return;
    try {
      const data = await api.get("/bock/today");
      // Owner case: my own Bock (may be accepted)
      const myOwnBock = data.find(b => b.personUuid === user.personUuid) ?? null;
      // Requester case: I'm the accepted match partner of someone else's Bock
      const myAcceptedPartnerBock = data.find(b => b.accepted && b.matchPartnerUuid === user.personUuid) ?? null;
      setMyBock(myOwnBock || myAcceptedPartnerBock);
      setOthersWithBockCount(data.filter(b => b.personUuid !== user.personUuid && !b.accepted).length);
    } catch {
      // silently ignore
    }
  }, [api, user]);

  useEffect(() => {
    loadBock();
    const interval = setInterval(loadBock, 30000);
    const onVisible = () => { if (document.visibilityState === "visible") loadBock(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadBock]); // eslint-disable-line

  const handleSetBock = async (timeFrom, timeTo) => {
    setShowBockModal(false);
    setBockError(null);
    try {
      await api.post("/bock", { timeFrom, timeTo });
      await loadBock();
    } catch {
      setBockError("Fehler beim Setzen des Bocks.");
    }
  };

  const handleRemoveBock = async () => {
    if (!myBock) return;
    setBockError(null);
    try {
      await api.delete(`/bock/${myBock.id}`);
      setMyBock(null);
    } catch {
      setBockError("Fehler beim Entfernen des Bocks.");
    }
  };

  const combinedAppointments = useMemo(() => {
    const workDutyItems = myWorkDuties.map((duty) => ({
      type: "WORK_DUTY",
      id: `workduty-${duty.id}`,
      sortDate: new Date(`${duty.date}T${duty.timeFrom}`),
      duty,
    }));
    const clubEventItems = myClubEvents.map((event) => ({
      type: "CLUB_EVENT",
      id: `clubevent-${event.id}`,
      sortDate: new Date(event.startTime),
      event,
    }));
    return [...workDutyItems, ...clubEventItems]
      .sort((a, b) => a.sortDate - b.sortDate)
      .slice(0, 5);
  }, [myWorkDuties, myClubEvents]);

  const registeredTournaments = Array.isArray(dashboard?.registeredTournaments)
    ? dashboard.registeredTournaments
    : [];

  return (
    <div className="home-container">
      <section className="home-hero">
        <img
          src={logo}
          alt="Tennisgemeinschaft Thedinghausen"
          className="home-logo"
        />
        <div className="home-hero-text">
          <h1 className="home-title">
            Tennisgemeinschaft Thedinghausen
          </h1>
        </div>
      </section>

      {showBockModal && (
        <BockModal onConfirm={handleSetBock} onClose={() => setShowBockModal(false)} />
      )}

      {(newsLoading || latestNews.length > 0) && (
        <section className="home-dashboard">
          <div className="home-dashboard-header">
            <h2 className="home-dashboard-title"><Link to="/news" className="home-latest-news-heading">Aktuelles</Link></h2>
          </div>
          {newsLoading
            ? [0, 1, 2].map(i => <div key={i} className="home-latest-news-skeleton" aria-hidden="true" />)
            : latestNews.map(item => (
            <a
              key={item.id}
              href={item.link}
              target="_blank"
              rel="noreferrer"
              className={`home-latest-news${readNewsIds.has(String(item.id)) ? " home-latest-news--read" : ""}`}
              onClick={() => markNewsRead(item.id)}
            >
              <span className="home-latest-news-title">{item.title}</span>
              <span className="home-latest-news-date">{formatDate(item.date)}</span>
            </a>
          ))}
        </section>
      )}

      {user && <section className="home-bock-banner">
        <a href="https://www.tennis04.com/thedinghausen" target="_blank" rel="noreferrer" className="home-bock-btn home-bock-btn--booking">
          🏟️ Platz buchen
        </a>
        <div className="home-bock-banner-right">
        {bockError && <p className="home-upcoming-error" style={{ marginBottom: 8 }}>{bockError}</p>}
        {myBock?.accepted ? (
          <button
            className="home-bock-btn home-bock-btn--accepted"
            onClick={() => navigate("/bock")}
            style={{ flexDirection: "column", gap: 4 }}
          >
            <span>🎾 Match bestätigt!</span>
            <span style={{ fontSize: 12, opacity: 0.85 }}>
              mit {myBock.personUuid === user.personUuid ? myBock.matchPartnerName : myBock.personName} · {myBock.matchTime?.substring(0, 5)} Uhr
            </span>
          </button>
        ) : myBock?.hasPendingRequests ? (
          <button
            className="home-bock-btn home-bock-btn--pending"
            onClick={() => navigate("/bock")}
            style={{ flexDirection: "column", gap: 4 }}
          >
            <span>🎾 Du hast Bock!</span>
            <span style={{ fontSize: 12, opacity: 0.85 }}>
              {myBock.timeFrom?.substring(0, 5)} – {myBock.timeTo?.substring(0, 5)} Uhr · Neue Anfrage!
            </span>
          </button>
        ) : myBock ? (
          <div className="home-bock-active">
            <div className="home-bock-active-info">
              <span className="home-bock-active-label">Du hast Bock!</span>
              <span className="home-bock-active-time">
                {myBock.timeFrom?.substring(0, 5)} – {myBock.timeTo?.substring(0, 5)} Uhr
              </span>
            </div>
            <div className="home-bock-active-actions">
              <button className="home-bock-btn home-bock-btn--outline" onClick={() => setShowBockModal(true)}>
                Ändern
              </button>
              <button className="home-bock-btn home-bock-btn--remove" onClick={handleRemoveBock}>
                Zurückziehen
              </button>
            </div>
          </div>
        ) : othersWithBockCount > 0 ? (
          <button
            className="home-bock-btn home-bock-btn--primary"
            onClick={() => navigate("/bock")}
            style={{ flexDirection: "column", gap: 4 }}
          >
            <span>🎾 Ich hab Bock!</span>
            <span style={{ fontSize: 12, opacity: 0.85 }}>
              {othersWithBockCount} {othersWithBockCount === 1 ? "weiterer hat" : "weitere haben"} Bock
            </span>
          </button>
        ) : (
          <button className="home-bock-btn home-bock-btn--primary" onClick={() => setShowBockModal(true)}>
            🎾 Ich hab Bock!
          </button>
        )}
        </div>
      </section>}

      {user && combinedAppointments.length > 0 && (
        <section className="home-dashboard" style={{ marginTop: 24 }}>
          <div className="home-dashboard-header">
            <h2 className="home-dashboard-title">Termine</h2>
          </div>
          {(workDutiesLoading || clubEventsLoading) ? (
            <p className="home-upcoming-info">Lade Termine...</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {combinedAppointments.map((item) => {
                if (item.type === "WORK_DUTY") {
                  const duty = item.duty;
                  const reached = duty.confirmedCount >= duty.minParticipants;
                  return (
                    <Link
                      key={item.id}
                      to="/arbeitsdienst"
                      className={`home-workduty${duty.myStatus ? ' home-workduty--answered' : ''}`}
                    >
                      <div className="home-workduty-title">
                        <span className="home-appointment-icon" aria-hidden="true">🧹</span>
                        {duty.title}
                      </div>
                      <div className="home-workduty-date">{formatDate(duty.date)} · {duty.timeFrom.substring(0, 5)} Uhr</div>
                      <div className="home-workduty-footer">
                        <span className={`home-workduty-count${reached ? ' home-workduty-count--reached' : ''}`}>
                          {reached && '✓ '}{duty.confirmedCount} von mind. {duty.minParticipants} zugesagt
                          {reached && ' – Mindestanzahl erreicht'}
                        </span>
                        {duty.myStatus && (
                          <span className={`home-workduty-mystatus home-workduty-mystatus--${duty.myStatus === 'CONFIRMED' ? 'confirmed' : 'declined'}`}>
                            {duty.myStatus === 'CONFIRMED' ? 'Zugesagt' : 'Abgesagt'}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                }

                const event = item.event;
                return (
                  <Link
                    key={item.id}
                    to="/vereinstermine"
                    className={`home-workduty${event.myStatus ? ' home-workduty--answered' : ''}`}
                  >
                    <div className="home-workduty-title">
                      <span className="home-appointment-icon" aria-hidden="true">📅</span>
                      {event.title}
                    </div>
                    <div className="home-workduty-date">{formatDateTime(event.startTime)}</div>
                    {event.registrationDeadline && (
                      <div className="home-workduty-footer">
                        <span className="home-workduty-count">
                          {event.confirmedCount} zugesagt
                          {event.registrationClosed
                            ? ' · Anmeldefrist abgelaufen'
                            : ` · Anmeldefrist: ${formatDate(event.registrationDeadline)}`}
                        </span>
                        {event.myStatus && (
                          <span className={`home-workduty-mystatus home-workduty-mystatus--${event.myStatus === 'CONFIRMED' ? 'confirmed' : 'declined'}`}>
                            {event.myStatus === 'CONFIRMED' ? 'Zugesagt' : 'Abgesagt'}
                          </span>
                        )}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      )}

      {user && myTeams.length > 0 && (
        <section className="home-dashboard" style={{ marginTop: 24 }}>
          <div className="home-dashboard-header">
            <h2 className="home-dashboard-title">Meine Mannschaften</h2>
          </div>
          {teamsLoading ? (
            <p className="home-upcoming-info">Lade Mannschaftstermine...</p>
          ) : (
            <div className="home-dashboard-grid">
              {myTeams.map(team => {
                const matches = myTeamMatches[team.id] || [];
                return (
                  <article key={team.id} className="home-dashboard-card">
                    <h3 className="home-dashboard-card-title">
                      <Link to={`/mannschaften/${team.id}`} className="home-inline-link">
                        {team.name}
                      </Link>
                      {team.followedByCurrentUser
                        && !team.captainForCurrentUser
                        && !(team.players || []).some(p => p.id === user?.personId) && (
                        <span className="groupphase-status-badge" style={{ marginLeft: 8, fontSize: 11 }}>
                          Gefolgt
                        </span>
                      )}
                    </h3>
                    <div className="home-dashboard-card-content">
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                        {matches.map(match => (
                          <Link
                            key={match.matchId}
                            to={`/mannschaften/${team.id}?matchId=${match.matchId}`}
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            <div style={{ borderLeft: "3px solid #4CAF50", paddingLeft: 10, fontSize: 13, cursor: "pointer" }}>
                              <div style={{ fontWeight: "bold" }}>{match.homeTeam} vs. {match.guestTeam}</div>
                              <div style={{ color: "#666" }}>{formatDateTime(match.matchDateTime)}</div>
                              {match.venue && <div style={{ color: "#888" }}>{match.venue}</div>}
                              {match.myAvailability && (
                                <div style={{ marginTop: 4, fontSize: 12 }}>
                                  Meine Antwort:{" "}
                                  <span style={{ color: match.myAvailability === "AVAILABLE" ? "#4CAF50" : "#e65100", fontWeight: "bold" }}>
                                    {match.myAvailability === "AVAILABLE" ? "Zugesagt" : "Abgesagt"}
                                  </span>
                                </div>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                    <p style={{ marginTop: "auto", paddingTop: "1rem", fontSize: 13 }}>
                      <Link to={`/mannschaften/${team.id}`} className="home-inline-link">
                        Alle Termine der Mannschaft →
                      </Link>
                    </p>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}

      {user && !dashboardLoading && !dashboardError && registeredTournaments.length > 0 && (
        <section className="home-dashboard">
          <div className="home-dashboard-header">
            <h2 className="home-dashboard-title">Meine Turniere</h2>
          </div>
          <div style={{display: "flex", flexDirection: "column", gap: 6}}>
            {registeredTournaments.map((tournament) => (
              <TournamentSummaryRow key={tournament.tournamentId} tournament={tournament} />
            ))}
          </div>
        </section>
      )}

      {!user && (
        <section className="home-info-panel">
          <p className="home-info-panel-text">
            Nach dem <Link to="/login" className="home-inline-link">Login</Link> sehen Sie hier zusätzlich Ihre
            angemeldeten Turniere und Ihre noch offenen Gruppenspiele.
          </p>
        </section>
      )}

      <section className="home-upcoming">
        <div className="home-dashboard-header">
          <h2 className="home-dashboard-title">Anstehende Matches</h2>
        </div>

        {loadingMatches && (
          <p className="home-upcoming-info">Lade anstehende Matches ...</p>
        )}
        {!loadingMatches && matchesError && (
          <p className="home-upcoming-error">{matchesError}</p>
        )}
        {!loadingMatches && !matchesError && upcomingMatches.length === 0 && (
          <p className="home-upcoming-info">
            Aktuell sind keine Matches geplant.
          </p>
        )}
        {!loadingMatches && !matchesError && upcomingMatches.length > 0 && (
          <div className="home-upcoming-list">
            {groupMatchesByDay(upcomingMatches).map(({ dayLabel, matches: dayMatches }) => (
              <div key={dayLabel} className="home-match-day-group">
                <div className="home-match-day-label">{dayLabel}</div>
                {dayMatches.map((match) => {
              const relation = match.relationToCurrentUser ?? "NONE";
              const isOwnMatch = relation !== "NONE";
              const isToday = dayLabel === "Heute";
              const isHome = match.homeMatch;
              const canEnterResult = match.source === "TOURNAMENT"
                && match.tournamentId != null
                && (relation === "PLAYER" || relation === "TEAM_MEMBER" || isUserAdmin(user));
              const resultEntryPath = canEnterResult
                ? `/tournaments?tournamentId=${match.tournamentId}&matchId=${match.matchId}${match.groupId != null ? `&groupId=${match.groupId}` : ""}`
                : null;
              const goToResultEntry = () => navigate(resultEntryPath);

              return (
                <div
                  key={buildUpcomingMatchKey(match)}
                  className={[
                    "home-match-card",
                    // Eigene Spiele werden immer grün hervorgehoben; die Heim-/Auswärts-Tönung
                    // (blau) gilt nur für fremde Spiele, damit die Eigen-Markierung nicht überdeckt wird.
                    isOwnMatch
                      ? "home-match-card--user"
                      : (isHome ? "home-match-card--home" : ""),
                    isToday ? "home-match-card--today" : "",
                    canEnterResult ? "home-match-card--clickable" : "",
                  ].filter(Boolean).join(" ")}
                  {...(canEnterResult ? {
                    role: "button",
                    tabIndex: 0,
                    onClick: goToResultEntry,
                    onKeyDown: (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        goToResultEntry();
                      }
                    },
                  } : {})}
                >
                  <div className="home-match-header">
                    <span className="home-match-tournament">
                      {match.tournamentName ?? "Turnier"}
                      {match.tournamentYear ? ` ${match.tournamentYear}` : ""}
                    </span>
                    <div className="home-match-header-badges">
                      {match.round && (
                        <span className="home-match-round">{formatRoundLabel(match.round, match.groupName)}</span>
                      )}
                      {isOwnMatch && (
                        <span className="home-match-relation-badge">
                          {formatRelationLabel(relation)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="home-match-players">
                    <span>{match.player1Name}</span>
                    <span className="home-match-vs">vs.</span>
                    <span>{match.player2Name}</span>
                  </div>
                  <div className="home-match-meta">
                    <span className="home-match-time">
                      {match.startTime && formatTime(match.startTime)}
                    </span>
                    {match.courtName && (
                      <span className="home-match-court">
                        {match.source === "TNB" ? "Ort" : "Platz"}: {match.courtName}
                      </span>
                    )}
                    {canEnterResult && (
                      <span className="home-match-enter-result">Ergebnis eintragen →</span>
                    )}
                  </div>
                </div>
              );
                })}
              </div>
            ))}
          </div>
        )}
      </section>

      <p className="home-footer-link-wrap">
        <Link to="/about" className="home-inline-link">
          Mehr über das Turnier und den Verein
        </Link>
        {" · "}
        <Link to="/impressum" className="home-inline-link">
          Impressum
        </Link>
        {" · "}
        <Link to="/datenschutz" className="home-inline-link">
          Datenschutz
        </Link>
      </p>
    </div>
  );
};

const TournamentSummaryRow = ({tournament}) => {
  const to = `/tournaments?tournamentId=${tournament.tournamentId}`;
  const name = `${tournament.tournamentName}${tournament.tournamentYear ? ` ${tournament.tournamentYear}` : ""}`;

  let borderColor = "#4CAF50";
  let info = null;

  if (tournament.winnerTeamName) {
    borderColor = "#f9a825";
    info = (
      <span style={{fontSize: 12, whiteSpace: "nowrap", color: "#f57f17", fontWeight: 600}}>
        🏆 Sieger: {tournament.winnerTeamName}
      </span>
    );
  } else if (tournament.koPhaseStarted) {
    borderColor = tournament.eliminatedInKo ? "#aaa" : "#1565c0";
    info = (
      <span style={{fontSize: 12, whiteSpace: "nowrap", color: tournament.eliminatedInKo ? "#999" : "#1565c0"}}>
        {tournament.currentKoRound ?? "K.O.-Phase"}
        {tournament.eliminatedInKo ? " · Ausgeschieden" : " · Noch dabei"}
      </span>
    );
  } else if (tournament.started) {
    const hasOpen = tournament.ownOpenGroupMatchCount > 0;
    borderColor = hasOpen ? "#e65100" : "#4CAF50";
    info = (
      <span style={{fontSize: 12, whiteSpace: "nowrap", color: hasOpen ? "#e65100" : "#4CAF50"}}>
        {hasOpen ? `${tournament.ownOpenGroupMatchCount} ${tournament.ownOpenGroupMatchCount === 1 ? "offenes Spiel" : "offene Spiele"}` : "Alle Spiele gespielt ✓"}
        {tournament.endDate && ` · Ende: ${formatDate(tournament.endDate)}`}
      </span>
    );
  } else if (tournament.registrationOpen) {
    borderColor = "#9c27b0";
    info = (
      <span style={{fontSize: 12, whiteSpace: "nowrap", color: "#7b1fa2"}}>
        {tournament.numberOfTeams} angemeldet
        {tournament.registrationDeadline && ` · Meldeschluss: ${formatDate(tournament.registrationDeadline)}`}
      </span>
    );
  }

  return (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 12px",
      borderLeft: `3px solid ${borderColor}`,
      background: "#fafafa",
      borderRadius: "0 4px 4px 0",
      gap: 12,
    }}>
      <Link to={to} className="home-inline-link" style={{fontWeight: 600, fontSize: 14}}>
        {name}
      </Link>
      {info}
    </div>
  );
};

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString("de-DE", {day: "2-digit", month: "2-digit", year: "numeric"});
};

const formatDateTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const groupMatchesByDay = (matches) => {
  const groups = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (const match of matches) {
    const d = match.startTime ? new Date(match.startTime) : null;
    let dayLabel;
    if (!d) {
      dayLabel = "Datum unbekannt";
    } else {
      const matchDay = new Date(d);
      matchDay.setHours(0, 0, 0, 0);
      const diffDays = Math.round((matchDay - today) / 86400000);
      if (diffDays === 0) dayLabel = "Heute";
      else if (diffDays === 1) dayLabel = "Morgen";
      else dayLabel = d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
    }
    const last = groups[groups.length - 1];
    if (last && last.dayLabel === dayLabel) {
      last.matches.push(match);
    } else {
      groups.push({ dayLabel, matches: [match] });
    }
  }
  return groups;
};

const formatTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) + " Uhr";
};

const buildUpcomingMatchKey = (match) =>
  `${match.source ?? "match"}-${match.matchId ?? "no-id"}-${match.startTime ?? "no-time"}-${match.player1Name ?? ""}-${match.player2Name ?? ""}`;

const ROUND_LABELS = {
  GROUP_PHASE: "Gruppenphase",
  ROUND_16: "Achtelfinale",
  QUARTER_FINAL: "Viertelfinale",
  SEMI_FINAL: "Halbfinale",
  FINAL: "Finale",
};

const formatRoundLabel = (round, groupName) => {
  const label = ROUND_LABELS[round] ?? round;
  return groupName ? `${label} · ${groupName}` : label;
};

const formatRelationLabel = (relation) => {
  switch (relation) {
    case "PLAYER":
      return "Eigenes Spiel";
    case "TEAM_MEMBER":
      return "Meine Mannschaft";
    case "FOLLOWER":
      return "Gefolgt";
    default:
      return "";
  }
};

export default Home;
